import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const DebugContainer = styled.div`
    position: fixed;
    top: 20px;
    left: 20px;
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 1rem;
    border-radius: 8px;
    max-width: 400px;
    z-index: 9999;
    font-family: monospace;
    font-size: 12px;
    border: 1px solid #333;
`;

const Button = styled.button`
    background: #6366f1;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    margin: 0.25rem;
    cursor: pointer;
    font-size: 12px;
`;

const LogItem = styled.div`
    margin: 0.25rem 0;
    padding: 0.25rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
`;

const AudioDebugger = ({ showByDefault = false }) => {
    const [logs, setLogs] = useState([]);
    const [isVisible, setIsVisible] = useState(showByDefault);
    const audioRef = useRef(null);

    const addLog = (message, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, { message, type, timestamp }]);
    };

    const testAudioFile = async () => {
        addLog('🔍 Testing audio file accessibility...');
        
        try {
            const response = await fetch('/audio/without-me.mp3', { method: 'HEAD' });
            addLog(`📡 Server response: ${response.status} ${response.statusText}`, response.ok ? 'success' : 'error');
            
            if (response.ok) {
                const contentType = response.headers.get('content-type');
                const contentLength = response.headers.get('content-length');
                addLog(`📄 Content-Type: ${contentType}`, 'info');
                addLog(`📏 Content-Length: ${contentLength} bytes`, 'info');
            }
        } catch (error) {
            addLog(`❌ Fetch error: ${error.message}`, 'error');
        }
    };

    const testAudioElement = () => {
        addLog('🎵 Testing audio element...');
        
        if (audioRef.current) {
            const audio = audioRef.current;
            addLog(`🔢 ReadyState: ${audio.readyState}`, 'info');
            addLog(`🌐 NetworkState: ${audio.networkState}`, 'info');
            addLog(`⏱️ Duration: ${audio.duration}`, 'info');
            addLog(`📁 CurrentSrc: ${audio.currentSrc}`, 'info');
            
            // Try to load the audio
            audio.load();
            addLog('🔄 Called audio.load()', 'info');
        }
    };

    const tryPlay = async () => {
        addLog('▶️ Attempting to play audio...');
        
        try {
            if (audioRef.current) {
                await audioRef.current.play();
                addLog('✅ Audio played successfully!', 'success');
            }
        } catch (error) {
            addLog(`❌ Play error: ${error.message}`, 'error');
        }
    };

    const clearLogs = () => {
        setLogs([]);
    };

    useEffect(() => {
        if (audioRef.current) {
            const audio = audioRef.current;
            
            audio.addEventListener('loadstart', () => addLog('📋 loadstart', 'info'));
            audio.addEventListener('loadedmetadata', () => addLog('📋 loadedmetadata', 'success'));
            audio.addEventListener('loadeddata', () => addLog('📋 loadeddata', 'success'));
            audio.addEventListener('canplay', () => addLog('📋 canplay', 'success'));
            audio.addEventListener('canplaythrough', () => addLog('📋 canplaythrough', 'success'));
            audio.addEventListener('error', (e) => addLog(`📋 error: ${e.message}`, 'error'));
            
            return () => {
                audio.removeEventListener('loadstart', () => {});
                audio.removeEventListener('loadedmetadata', () => {});
                audio.removeEventListener('loadeddata', () => {});
                audio.removeEventListener('canplay', () => {});
                audio.removeEventListener('canplaythrough', () => {});
                audio.removeEventListener('error', () => {});
            };
        }
    }, []);

    if (!isVisible) return null;

    return (
        <DebugContainer>
            <div style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
                🔧 Audio Debugger
                <button 
                    onClick={() => setIsVisible(false)} 
                    style={{ float: 'right', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                >
                    ✕
                </button>
            </div>
            
            <audio ref={audioRef} preload="metadata">
                <source src="/audio/without-me.mp3" type="audio/mpeg" />
            </audio>
            
            <div style={{ marginBottom: '1rem' }}>
                <Button onClick={testAudioFile}>Test File Access</Button>
                <Button onClick={testAudioElement}>Test Audio Element</Button>
                <Button onClick={tryPlay}>Try Play</Button>
                <Button onClick={clearLogs}>Clear Logs</Button>
            </div>
            
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {logs.map((log, index) => (
                    <LogItem key={index} style={{ 
                        color: log.type === 'error' ? '#ef4444' : 
                               log.type === 'success' ? '#10b981' : '#94a3b8' 
                    }}>
                        <span style={{ opacity: 0.7 }}>[{log.timestamp}]</span> {log.message}
                    </LogItem>
                ))}
            </div>
        </DebugContainer>
    );
};

export default AudioDebugger; 